/**
 * FeeScheduleTable — the broker fee ledger for one contract (Feature 30).
 *
 * Rendered on BOTH contract views, fed the same props by each:
 *   - the wizard's Summary step (steps/ContractSummaryStep.js), for a Draft under review
 *   - the standalone Contract Detail page (pages/ContractDetail.js), after approval
 *
 * WHAT THIS SHOWS, AND WHY IT HAS TWO KINDS OF ROW
 * ------------------------------------------------
 * A broker fee is RAISED once but the expense is RECOGNISED over the contract term, so the ledger
 * carries both legs:
 *   - PAYMENT rows  — when the fee is actually settled (1 row once-off, or N on a cadence)
 *   - ACCRUAL rows  — the monthly expense spread, one per month of the contract's tenor
 * Both sides sum to the same struck fee. On dev contract 376 that is 4 quarterly payments of
 * 251.25 and 13 monthly accruals, each side totalling SZL 1,005.00.
 *
 * ⚠ ROW KIND COMES FROM `is_payment_event`, NEVER FROM THE NARRATION. fn_contract_fee_schedule_get
 * returns that flag precisely so consumers stop re-deriving it, and a narration test would be
 * wrong: `LIKE 'Broker Fee%'` also matches the pre-existing SYSTEM row 'Broker Fee Deduction'
 * (multiplier -1), which is not part of this ledger at all.
 *
 * ⚠ TOTALS COME FROM THE API, NOT FROM RE-SUMMING THE ROWS. The route already computes them
 * (rounding once at the end rather than accumulating rounded values). Re-summing here would let
 * the tab and the API drift apart on a rounding edge, which is exactly the class of "the totals
 * tie but the composition is wrong" defect this engine has produced five times.
 */

import React from 'react';
import {
  Alert,
  Box,
  Chip,
  Paper,
  Typography,
} from '@mui/material';
import DataTable from '../common/DataTable';
import usePagination from '../../hooks/usePagination';
import { versionChipProps } from '../../utils/scheduleVersion';
import { formatNumber, formatShortDate, formatRatePct } from '../../utils/formatters';

const num = (v) => (v == null || v === '' ? 0 : parseFloat(v));

function FeeScheduleTable({
  contract, rows = [], totals = {}, versions = [], currencyCode, showAllRows = false,
}) {
  const ccy = currencyCode || contract?.currency_code || '';
  // Prefix that renders "SZL " before an amount, or nothing when no currency is known.
  // Copied from AmortizationScheduleTable.js:411 — see the column definitions for why the
  // currency lives in the CELL here rather than in the header.
  const ccyPrefix = ccy ? `${ccy} ` : '';

  // Paginated on Contract Detail; the wizard Summary step passes showAllRows so a Draft under
  // review shows its whole schedule without the reviewer clicking through pages. Same split, and
  // the same 25 default, as AmortizationScheduleTable.js:54-60 — one convention, not two.
  // DataTable does the slicing and renders the pager itself when given this object.
  const pagination = usePagination({ defaultRowsPerPage: 25 });

  // The STRUCK fee, taken from the snapshot columns the broker dialog persisted — never
  // recomputed. See the header card below for why.
  const struckExcl = num(contract?.broker_fee_exclusive_amount);
  const struckVat = num(contract?.broker_fee_vat_amount);
  // Inclusive is snapshotted too, but fall back to excl + vat for a pre-T3-012 contract whose
  // snapshot predates that column rather than rendering a blank.
  const struckIncl = contract?.broker_fee_inclusive_amount != null
    ? num(contract.broker_fee_inclusive_amount)
    : struckExcl + struckVat;

  // NO_VAT is a deliberate tax decision (a hard 0%), not an absent rate — say which, exactly as
  // the capture dialog does, so a 0.00 VAT line is never read as a missing configuration.
  const isNoVat = contract?.broker_fee_vat_treatment === 'NO_VAT';
  const vatPct = contract?.broker_fee_vat_percent;
  const vatLabel = isNoVat
    ? 'VAT (0% — No-VAT)'
    : `VAT${vatPct != null ? ` (${formatRatePct(vatPct)}%)` : ''}`;

  // A version whose accruals do not sum to its struck total was not fully generated. The route
  // computes this (`is_reconciled`); surfacing it is the point — a silently short schedule is the
  // failure mode worth catching.
  const unreconciled = (versions || []).filter((v) => v && v.is_reconciled === false);

  // ============================================================================================
  // CUMULATIVE BALANCE — the fee's outstanding position, row by row.
  // ============================================================================================
  // Mirrors the amortization schedule's Outstanding Balance (AmortizationScheduleTable.js:292-323):
  // one pass over the WHOLE schedule, accruals ADD to the balance and payments SUBTRACT, rounded at
  // each step so the displayed figures are the ones that actually accumulate.
  //
  // For a fee the two legs answer: "how much of this fee has been recognised as expense but not yet
  // settled". It rises as the expense accrues and drops each time the broker is paid. On a fee that
  // ties, the final row lands on 0.00.
  //
  // ⚠ KEYED BY ROW id, NOT BY ARRAY INDEX. The amortization table indexes
  // `runningBalances[page * pageSize + index]` because it hand-rolls its own <TableBody> and knows
  // the page offset. DataTable hands a render function the index WITHIN THE PAGINATED SLICE, so the
  // same trick would restart the balance at every page boundary and silently show wrong figures on
  // page 2 onward. A Map keyed on id is immune to both pagination and any future column sort.
  //
  // ⚠ Uses the VAT-EXCLUSIVE amounts, matching the Amount (Excl) column — VAT is a separate
  // recoverable item, not part of the fee expense being carried.
  //
  // ⚠⚠ A NEGATIVE BALANCE IS CORRECT AND EXPECTED — DO NOT "FIX" IT WITH Math.abs OR A CLAMP.
  // It means PREPAID: the broker has been paid ahead of the expense accruing. That is the normal
  // state for a fee paid IN ADVANCE, which is the common case (fees are usually paid upfront).
  // Measured on dev contract 376 (quarterly, in advance): the 13 Aug payment lands before any
  // expense has accrued, so the position sits at -251.25 and stays negative for most of the term,
  // recovering to exactly 0.00 on the final row. Dev contract 48 (monthly, in advance) behaves the
  // same way, low point -155.22, final 0.00.
  // Sign convention confirmed by the user 2026-09-17: accrual - payment, the SAME direction as the
  // amortization Outstanding Balance sitting beside it on the same contract. Flipping it here
  // would make the two schedules disagree about which way "outstanding" points.
  const balanceById = React.useMemo(() => {
    const m = new Map();
    let balance = 0;
    for (const r of rows || []) {
      const accrual = num(r.accrual_amount);
      const payment = num(r.payment_amount);
      balance += accrual - payment;
      balance = Math.round(balance * 100) / 100;
      m.set(r.id, balance);
    }
    return m;
  }, [rows]);

  const columns = [
    {
      key: 'sequence_number',
      header: '#',
      align: 'right',
      sx: { width: 56 },
    },
    // VERSION — same position and same chip as the amortization schedule (Seq then Version).
    // ⚠ This becomes load-bearing the moment a fee is repriced: sp_fee_change_approve closes V1 and
    // opens V2, so a repriced ledger carries rows from BOTH versions. Without this column they
    // interleave with nothing saying which figures belong to the old fee and which to the new —
    // and the lifetime total legitimately equals neither (V1-accrued-to-date + V2 total).
    // The helper is SHARED (utils/scheduleVersion.js), not copied: contract_fee_versions.version_type
    // uses the same vocabulary as the amortization one.
    {
      key: 'version_number',
      header: 'Version',
      align: 'center',
      render: (r) => {
        const chip = versionChipProps(r.version_number, r.version_type);
        if (!chip) return '';
        return (
          <Chip
            size="small"
            label={chip.label}
            color={chip.color}
            variant="outlined"
            title={chip.title}
          />
        );
      },
    },
    // ⚠ TWO COLUMNS — From / To — NOT ONE "Period" RANGE. This matches the amortization schedule
    // (AmortizationScheduleTable.js:1008-1009 headers, :1376-1377 cells), which is the standard for
    // a schedule table in this app; the two tabs sit side by side on the same contract and were
    // reading differently for the same concept.
    //
    // ⚠⚠ AND THEY USE formatShortDate, NOT formatDate — THIS HALF IS A CORRECTNESS FIX, NOT STYLE.
    // formatShortDate (utils/formatters.js:89) normalises a plain date string to LOCAL NOON
    // ("substring(0,10) + 'T12:00:00'") specifically to avoid the timezone day-shift; formatDate
    // (:54) does a raw `new Date(date)` with no such guard. A date-only value from Postgres can
    // therefore render a day early under a negative UTC offset. That exact trap has bitten this
    // feature before — a stored 2026-08-13 read as the 12th via toISOString — so the fee tab must
    // use the guarded formatter the amortization tab already uses.
    // (It also brings the padding into line: en-GB 2-digit "08 Jul 2026" vs en-ZA "8 Jul 2026".)
    //
    // A payment row has no period — it is a point event — so these cells stay EMPTY rather than
    // rendering a "-" that reads like a missing value. formatShortDate's fallback defaults to '-',
    // hence the explicit { fallback: '' }.
    {
      key: 'period_start',
      header: 'From',
      render: (r) => (r.period_start ? formatShortDate(r.period_start, { fallback: '' }) : ''),
    },
    {
      key: 'period_end',
      header: 'To',
      render: (r) => (r.period_end ? formatShortDate(r.period_end, { fallback: '' }) : ''),
    },
    {
      key: 'payment_date',
      header: 'Payment Date',
      // Same guarded formatter — a payment DATE is the one field where a day-shift would be most
      // visible, since it is what the matching queue keys its overdue calculation on.
      render: (r) => (r.payment_date ? formatShortDate(r.payment_date, { fallback: '' }) : ''),
    },
    // TYPE — the fee ledger's equivalent of the amortization schedule's "Flow" column, and it sits
    // in the SAME position (after Payment Date, before Narration) so the two tabs read alike.
    {
      key: 'kind',
      header: 'Type',
      render: (r) => (
        <Chip
          size="small"
          label={r.is_payment_event ? 'Payment' : 'Accrual'}
          color={r.is_payment_event ? 'primary' : 'default'}
          variant={r.is_payment_event ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      key: 'narration',
      header: 'Narration',
    },
    // SETTLEMENT STATUS (T3-143). Copies the amortization Status cell
    // (AmortizationScheduleTable.js:1397-1421) minus its variance chip — fee matching is
    // all-or-nothing in v1, so there is no variance to render.
    //
    // ⚠⚠ THIS COLUMN ASSERTS THAT MONEY CHANGED HANDS, SO IT MUST COME FROM SETTLEMENT DATA AND
    // NOTHING ELSE. An earlier cut of this tab rendered "Paid (4 payments)" from the SCHEDULED
    // total on a DRAFT contract whose rows were future-dated — a confident, entirely false claim.
    // The status now comes from fn_contract_fee_schedule_get, which resolves it by EXISTS against
    // contract_fee_payments. Never derive it from a date being in the past, from a total, or from
    // the row simply existing.
    //
    // ⚠ NULL renders as "-", never "Pending". The API returns NULL on every accrual row because an
    // accrual is expense recognition, not money owed — so it has no settlement state to report.
    // "-" for an unknown/inapplicable status is the house convention
    // (AmortizationScheduleTable.js:1400-1401).
    {
      key: 'status',
      header: 'Status',
      align: 'center',
      render: (r) => {
        if (!r.status) {
          return <Typography variant="caption" color="textSecondary">-</Typography>;
        }
        return (
          <Chip
            label={r.status}
            size="small"
            color={r.status === 'Paid' ? 'success' : 'warning'}
            variant={r.status === 'Paid' ? 'filled' : 'outlined'}
            sx={{ minWidth: 70 }}
          />
        );
      },
    },
    {
      key: 'days_in_period',
      header: 'Days',
      align: 'right',
      render: (r) => (r.days_in_period == null ? '' : r.days_in_period),
    },
    // THE MONEY BLOCK — Excl / VAT / Incl, deliberately the same three columns and the same
    // header names as the broker capture table (BrokerSection.js:604-621), so the screen where a
    // fee is agreed and the ledger where it is accounted for read identically.
    //
    // ⚠ ONE "Exclusive" COLUMN, NOT SEPARATE ACCRUAL AND PAYMENT COLUMNS. A row carries EITHER an
    // accrual or a payment — never both (chk_cfs_row_kind enforces it) — so two columns left each
    // one ~75% empty while saying nothing the Type chip does not already say.
    //
    // ⚠ CURRENCY GOES IN THE CELL, NOT THE HEADER. The house rule is currency in the header XOR
    // in the cell, never both: AmortizationScheduleTable.js:443-445 states it from the other side
    // ("already shown in the column headers, so the cells omit the prefix"), and BrokerSection
    // puts it in the cell for exactly these three columns. Repeating "(SZL)" in every header of a
    // single-currency table is noise.
    {
      key: 'amount_excl',
      header: 'Amount (Excl)',
      align: 'right',
      // Whichever leg this row is. Both are VAT-EXCLUSIVE in the database: net_amount duplicates
      // them rather than adding a third dimension, and no inclusive figure is stored on any row.
      render: (r) => {
        const excl = num(r.accrual_amount) || num(r.payment_amount);
        return excl ? `${ccyPrefix}${formatNumber(excl)}` : '';
      },
    },
    {
      key: 'vat_amount',
      header: 'Amount (VAT)',
      align: 'right',
      render: (r) => (num(r.vat_amount) ? `${ccyPrefix}${formatNumber(num(r.vat_amount))}` : ''),
    },
    {
      key: 'amount_incl',
      header: 'Amount (Incl)',
      align: 'right',
      // DERIVED — excl + VAT, rounded once. ⚠ On a payment row this is the figure the matching
      // queue already calls `scheduled_amount` (fn_fee_payment_get_pending): 251.25 + 37.69 =
      // 288.94. Before this column existed the tab and the Broker Fees matching queue showed
      // different numbers for the same row.
      render: (r) => {
        const excl = num(r.accrual_amount) || num(r.payment_amount);
        if (!excl) return '';
        return `${ccyPrefix}${formatNumber(Math.round((excl + num(r.vat_amount)) * 100) / 100)}`;
      },
    },
    // CUMULATIVE BALANCE — last column, exactly where the amortization schedule puts its
    // Outstanding Balance, and bold for the same reason: it is the figure a reader scans down.
    // Accrued-but-unsettled fee expense; 0.00 on the final row of a schedule that ties.
    {
      key: 'balance',
      header: 'Balance',
      align: 'right',
      render: (r) => {
        const b = balanceById.get(r.id);
        if (b === undefined) return '';
        return <strong>{`${ccyPrefix}${formatNumber(b)}`}</strong>;
      },
    },
  ];

  // A brokered contract with no rows is a normal state, not an error — the schedule is generated
  // by an explicit action. Say which action, rather than showing a bare empty table.
  if (!rows.length) {
    return (
      <Box>
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No fee schedule generated yet.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Use “Generate Schedule” on the Review step to create it.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      {unreconciled.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {unreconciled.length === 1 ? 'A fee version does' : `${unreconciled.length} fee versions do`}
          {' '}not reconcile — the accruals do not sum to the struck fee, which means the schedule
          was not fully generated. Regenerate it before relying on these figures.
        </Alert>
      )}

      {/* THE STRUCK FEE — deliberately the SAME three-row shape as the broker capture dialog
          (BrokerSection.js), so the figure a user agreed in the dialog and the figure shown here
          are visually identical and cannot be mistaken for different numbers.

          ⚠ READS THE SNAPSHOT COLUMNS, IT DOES NOT RECOMPUTE. broker_fee_exclusive_amount /
          _vat_amount / _inclusive_amount are what was AGREED at submission and are never
          re-struck by a later capital change (the struck-once rule in CLAUDE.md). Recomputing
          capital × % here would silently disagree with the contract on any repriced or
          capital-amended deal, and would also have to re-implement the NO_VAT treatment (a hard
          0%, not the country rate) and the no-rate-configured case. */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">Exclusive (capital × %)</Typography>
          <Typography variant="body2">{ccy} {formatNumber(struckExcl)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">{vatLabel}</Typography>
          <Typography variant="body2">{ccy} {formatNumber(struckVat)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" fontWeight="bold">Inclusive</Typography>
          <Typography variant="body2" fontWeight="bold">{ccy} {formatNumber(struckIncl)}</Typography>
        </Box>
      </Paper>

      {/* WHAT THE LEDGER ACCOUNTS FOR — presented as FLOW CARDS, matching the amortization
          schedule's own totals (AmortizationScheduleTable.js:779-826: Capital Inflow / Capital
          Outflow / Gross Interest, each a caption + coloured figure + directional sub-caption).

          ⚠⚠ THESE ARE SCHEDULED AMOUNTS. NEVER LABEL THEM "PAID".
          An earlier cut of this card read "Paid (4 payments)" — which asserted money had changed
          hands on a contract that was still a DRAFT with future-dated rows. Nothing in
          contract_fee_schedule records settlement: it has 23 columns and not one of them is a
          paid/matched/status/allocated field (the nearest, journal_exported_at, is GL-export
          state, not cash). Broker fee settlement is not tracked at all yet — see the deferred
          matching work.
          The amortization schedule has exactly the same property and solves it the same way: its
          totals use FLOW language only, and paid-ness lives in a per-row Status chip fed by an
          EXISTS onto contract_payments. Fees have no equivalent source, so they get no such chip —
          and per that standard (AmortizationScheduleTable.js:1400-1401) an unknown status renders
          as a plain "-", never an asserted one. */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'nowrap', mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center', p: 1, backgroundColor: '#fff3e0', borderRadius: 1, border: '1px solid #ffcc80' }}>
          <Typography variant="caption" color="textSecondary">Fee Accrual</Typography>
          <Typography variant="body1" fontWeight="bold" color="warning.dark" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {ccy} {formatNumber(num(totals.total_accrued))}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {totals.accrual_rows ?? 0} {totals.accrual_rows === 1 ? 'entry' : 'entries'} over the term
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center', p: 1, backgroundColor: '#ffebee', borderRadius: 1, border: '1px solid #ef9a9a' }}>
          <Typography variant="caption" color="textSecondary">Fee Outflow</Typography>
          <Typography variant="body1" fontWeight="bold" color="error.main" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            - {ccy} {formatNumber(num(totals.total_payments))}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {totals.payment_events ?? 0} {totals.payment_events === 1 ? 'event' : 'events'} to broker
          </Typography>
        </Box>
      </Box>

      {/* Passing the pagination object is all that is needed — DataTable slices the rows and
          renders <TablePagination> itself (DataTable.js:53,110). Omitting it renders every row
          with no pager, which is exactly what the wizard Summary step wants. */}
      <DataTable
        columns={columns}
        data={rows}
        keyField="id"
        dense
        pagination={showAllRows ? undefined : pagination}
        emptyMessage="No fee schedule rows."
      />
    </Box>
  );
}

export default FeeScheduleTable;
